/* Sloos Fashion — shared behaviour. Vanilla only, no dependencies. */
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var scrollLocks = 0;

  var Sloos = window.Sloos || (window.Sloos = {});

  Sloos.lockScroll = function () {
    if (scrollLocks === 0) {
      var width = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      if (width > 0) document.body.style.paddingRight = width + 'px';
    }
    scrollLocks++;
  };

  Sloos.unlockScroll = function () {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks === 0) {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
  };

  /* Traps tab focus inside an element and returns it to the trigger on release. */
  Sloos.trapFocus = function (container, trigger) {
    var previous = trigger || document.activeElement;

    function onKeydown(event) {
      if (event.key !== 'Tab') return;
      var items = Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetParent !== null;
      });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeydown);

    var target = container.querySelector('[autofocus]') || container.querySelector(FOCUSABLE);
    if (target) {
      window.requestAnimationFrame(function () {
        target.focus();
      });
    }

    return function release() {
      container.removeEventListener('keydown', onKeydown);
      if (previous && typeof previous.focus === 'function') previous.focus();
    };
  };

  Sloos.formatMoney = function (cents) {
    var format = Sloos.moneyFormat || '${{amount}}';
    var value = (cents / 100).toFixed(2);

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount':
          return withCommas(value, '.', ',');
        case 'amount_no_decimals':
          return withCommas(Math.round(cents / 100).toString(), '.', ',');
        case 'amount_with_comma_separator':
          return withCommas(value.replace('.', ','), ',', '.');
        case 'amount_no_decimals_with_comma_separator':
          return withCommas(Math.round(cents / 100).toString(), ',', '.');
        case 'amount_with_space_separator':
          return withCommas(value.replace('.', ','), ',', ' ');
        case 'amount_no_decimals_with_space_separator':
          return withCommas(Math.round(cents / 100).toString(), ',', ' ');
        default:
          return value;
      }
    });

    function withCommas(number, decimalMark, thousandsMark) {
      var parts = number.split(decimalMark);
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousandsMark);
      return parts.length > 1 ? whole + decimalMark + parts[1] : whole;
    }
  };

  Sloos.debounce = function (fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  };

  /* ---------------- Reveal on scroll ---------------- */

  function initReveal() {
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var targets = document.querySelectorAll('[data-reveal]');
    if (!targets.length) return;

    if (reduce || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(targets, function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    Array.prototype.forEach.call(targets, function (el) {
      observer.observe(el);
    });
  }

  /* ---------------- Quantity steppers ---------------- */

  function initQuantity(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-qty]').forEach(function (widget) {
      if (widget.dataset.qtyReady === 'true') return;
      widget.dataset.qtyReady = 'true';

      var input = widget.querySelector('input');
      if (!input) return;

      widget.addEventListener('click', function (event) {
        var button = event.target.closest('[data-qty-step]');
        if (!button) return;
        event.preventDefault();

        var step = parseInt(button.dataset.qtyStep, 10);
        var min = parseInt(input.getAttribute('min') || '0', 10);
        var max = input.getAttribute('max') ? parseInt(input.getAttribute('max'), 10) : Infinity;
        var next = Math.min(max, Math.max(min, (parseInt(input.value, 10) || 0) + step));

        if (next === parseInt(input.value, 10)) return;
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  Sloos.initQuantity = initQuantity;

  /* ---------------- Localization forms ---------------- */

  if (!customElements.get('localization-form')) {
    customElements.define(
      'localization-form',
      class LocalizationForm extends HTMLElement {
        connectedCallback() {
          this.toggle = this.querySelector('.localization__toggle');
          this.list = this.querySelector('.localization__list');
          this.input = this.querySelector('input[name="country_code"], input[name="locale_code"]');
          this.form = this.querySelector('form');

          if (this.toggle && this.list) {
            this.toggle.addEventListener('click', this.onToggle.bind(this));
            document.addEventListener('click', this.onOutside.bind(this));
            this.addEventListener('keydown', function (event) {
              if (event.key === 'Escape') this.close();
            }.bind(this));
          }

          this.addEventListener('click', this.onSelect.bind(this));
        }

        onToggle() {
          this.list.hidden ? this.open() : this.close();
        }

        open() {
          this.list.hidden = false;
          this.toggle.setAttribute('aria-expanded', 'true');
        }

        close() {
          if (!this.list) return;
          this.list.hidden = true;
          this.toggle.setAttribute('aria-expanded', 'false');
        }

        onOutside(event) {
          if (!this.contains(event.target)) this.close();
        }

        onSelect(event) {
          var option = event.target.closest('.localization__option');
          if (!option || !this.input || !this.form) return;
          event.preventDefault();
          this.input.value = option.value;
          this.form.submit();
        }
      }
    );
  }

  /* ---------------- Accordions (details/summary need no JS) ---------------- */

  /* ---------------- Sticky dock bar ---------------- */

  function initDock() {
    if (document.querySelector('.dock-bar')) document.body.classList.add('has-dock');
  }

  /* ---------------- Wishlist (localStorage for guests) ---------------- */

  var WISHLIST_KEY = 'sloos-wishlist';

  var wishlist = {
    read: function () {
      try {
        return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
      } catch (e) {
        return [];
      }
    },
    write: function (ids) {
      try {
        localStorage.setItem(WISHLIST_KEY, JSON.stringify(ids));
      } catch (e) {
        /* private mode — the wishlist is best-effort for guests */
      }
      document.dispatchEvent(new CustomEvent('sloos:wishlist:change', { detail: { ids: ids } }));
    },
    has: function (id) {
      return this.read().indexOf(String(id)) !== -1;
    },
    toggle: function (id) {
      var ids = this.read();
      var key = String(id);
      var index = ids.indexOf(key);
      if (index === -1) ids.push(key);
      else ids.splice(index, 1);
      this.write(ids);
      return index === -1;
    }
  };

  Sloos.wishlist = wishlist;

  function paintWishlist() {
    var ids = wishlist.read();

    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (button) {
      var active = ids.indexOf(String(button.dataset.wishlistToggle)) !== -1;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    document.querySelectorAll('[data-wishlist-count]').forEach(function (badge) {
      badge.textContent = ids.length;
      badge.hidden = ids.length === 0;
    });
  }

  function initWishlist() {
    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-wishlist-toggle]');
      if (!button) return;
      event.preventDefault();
      wishlist.toggle(button.dataset.wishlistToggle);
      paintWishlist();
    });

    document.addEventListener('sloos:wishlist:change', paintWishlist);
    document.addEventListener('sloos:wishlist:paint', paintWishlist);
    paintWishlist();
  }

  /* ---------------- Boot ---------------- */

  /* One failing initialiser must not take the others down with it. Without
     this, an error in, say, the reveal observer silently disables the wishlist
     and the quantity steppers on the same page. */
  /* Anything that sticks below the header needs the header's real height, and
     that changes with the logo size, the second nav row and the viewport — so
     measure it rather than hard-coding an offset. Reports 0 when the merchant
     has turned the sticky header off, which is what a non-sticky header is
     worth to a sticky child. */
  function initStickyOffset() {
    var header = document.querySelector('.header');
    if (!header) return;

    var section = header.closest('.shopify-section') || header;

    function measure() {
      var stuck = window.getComputedStyle(section).position === 'sticky';
      document.documentElement.style.setProperty(
        '--sloos-header',
        (stuck ? section.offsetHeight : 0) + 'px'
      );
    }

    measure();
    window.addEventListener('resize', Sloos.debounce(measure, 150));
    document.addEventListener('shopify:section:load', measure);
  }

  /* Size tables come through the product description as plain <table> markup,
     and a table of eight sizes is wider than a phone. CSS alone cannot wrap an
     element it does not own, so give each one a scrolling parent. Idempotent:
     the import may already ship a wrapper, and sections re-initialise. */
  function initDescriptionTables() {
    var tables = document.querySelectorAll('.rte table');

    Array.prototype.forEach.call(tables, function (table) {
      var parent = table.parentNode;
      if (!parent || parent.classList.contains('rte__table-scroll')) return;

      var scroller = document.createElement('div');
      scroller.className = 'rte__table-scroll';
      parent.insertBefore(scroller, table);
      scroller.appendChild(table);
    });
  }

  /* The product gallery has no JavaScript at all any more. A radio per
     photograph holds which one is showing and CSS does the switching, because
     four rewrites of a click handler never worked on the live shop. */

  function init() {
    [
      ['reveal', initReveal],
      ['quantity', initQuantity],
      ['dock', initDock],
      ['sticky offset', initStickyOffset],
      ['description tables', initDescriptionTables],
      ['wishlist', initWishlist]
    ].forEach(function (entry) {
      try {
        entry[1]();
      } catch (error) {
        console.error('[theme] ' + entry[0] + ' failed to start:', error);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
